import type { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  children?: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'brand-cta text-background-50 focus-visible:ring-primary-400',
  secondary:
    'bg-secondary-500 text-background-50 hover:bg-secondary-600 focus-visible:ring-secondary-400',
  accent:
    'bg-accent-500 text-background-50 hover:bg-accent-600 focus-visible:ring-accent-400',
  outline:
    'border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-100 focus-visible:ring-primary-400',
  ghost:
    'bg-transparent text-foreground-700 hover:bg-background-200 focus-visible:ring-primary-400',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-sm gap-2',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  icon,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      className={`inline-flex items-center justify-center rounded-md font-label font-medium whitespace-nowrap transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...rest}
    >
      {icon ? (
        <span className="w-4 h-4 flex items-center justify-center text-base">
          <i className={icon} />
        </span>
      ) : null}
      {children}
    </button>
  );
}